/* PDP buy form.
   - Resolves the variant from the option radios against the embedded
     variants JSON; updates price, availability, URL, and button state.
   - Sold-out variants keep their option visible but struck through (§4.8)
     and the buy button clearly reflects their unavailable state.
   - Add to bag goes through Dotto.cartAdd, which opens the cart drawer.
   - Emits `variant:changed` (detail: {variant}) for the sticky bar and
     complete-the-print.

   ===================================================================== */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.idInput = this.querySelector('[data-variant-id]');
    this.submitBtn = this.querySelector('[data-submit]');
    this.submitLabel = this.querySelector('[data-submit-label]');
    this.errorEl = this.querySelector('[data-form-error]');
    this.priceEl = document.querySelector(`[data-section-id="${this.dataset.sectionId}"] [data-price]`)
      || document.querySelector('[data-price]');
    this.isPreorder = this.hasAttribute('data-preorder');

    this.currentVariant = this.variants.find((v) => String(v.id) === this.idInput.value) || this.variants[0];

    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.querySelectorAll('.pdp__option input[type="radio"]').forEach((radio) => {
      radio.addEventListener('change', () => this.onOptionChange());
    });
    this.markAvailability();
    this.setBuyState(this.currentVariant && this.currentVariant.available ? 'available' : 'soldout');
  }

  selectedOptions() {
    return Array.from(this.querySelectorAll('.pdp__option')).map(
      (fieldset) => (fieldset.querySelector('input:checked') || {}).value
    );
  }

  resolveVariant() {
    const selection = this.selectedOptions();
    if (selection.length === 0) return this.variants[0];
    return this.variants.find((variant) =>
      variant.options.every((value, index) => value === selection[index])
    );
  }

  onOptionChange() {
    this.currentVariant = this.resolveVariant() || null;
    this.markAvailability();
    this.hideError();

    if (!this.currentVariant) {
      this.setBuyState('unavailable');
      return;
    }

    this.idInput.value = this.currentVariant.id;
    if (this.priceEl) {
      this.priceEl.innerHTML = `<span class="price price--large">${window.Dotto.formatMoney(this.currentVariant.price)}</span>`;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url);

    this.setBuyState(this.currentVariant.available ? 'available' : 'soldout');
    this.syncStockLine();
    document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant: this.currentVariant } }));
  }

  setBuyState(state) {
    const strings = window.Dotto.strings;
    if (state === 'available') {
      this.submitBtn.hidden = false;
      this.submitBtn.removeAttribute('aria-disabled');
      this.submitLabel.textContent = this.isPreorder ? strings.preorder : strings.addToBag;
    } else if (state === 'soldout') {
      this.submitBtn.hidden = false;
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitLabel.textContent = strings.soldOut;
    } else {
      this.submitBtn.hidden = false;
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitLabel.textContent = strings.soldOut;
    }
  }

  /* Strike through option values that resolve to a sold-out or missing
     variant, keeping the other positions' current selection fixed. */
  markAvailability() {
    const selection = this.selectedOptions();
    this.querySelectorAll('.pdp__option').forEach((fieldset, position) => {
      fieldset.querySelectorAll('.pdp__value').forEach((label) => {
        const value = label.querySelector('input').value;
        const candidate = this.variants.find((variant) =>
          variant.options.every((option, index) => (index === position ? option === value : option === selection[index]))
        );
        label.classList.toggle('is-unavailable', !candidate || !candidate.available);
      });
    });
  }

  syncStockLine() {
    const line = document.querySelector('[data-stock-line]');
    if (!line || !this.currentVariant) return;
    const available = this.currentVariant.available;
    line.textContent = available ? window.Dotto.strings.inStock : window.Dotto.strings.soldOut;
    line.classList.toggle('pdp__stock--out', !available);
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.currentVariant || !this.currentVariant.available) return;
    if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

    const strings = window.Dotto.strings;
    const restingLabel = this.isPreorder ? strings.preorder : strings.addToBag;
    this.submitBtn.setAttribute('aria-disabled', 'true');
    this.submitLabel.textContent = strings.adding;
    this.hideError();

    try {
      await window.Dotto.cartAdd([{ id: this.currentVariant.id, quantity: 1 }]);
      this.submitLabel.textContent = strings.added;
      setTimeout(() => {
        this.submitLabel.textContent = restingLabel;
        this.submitBtn.removeAttribute('aria-disabled');
      }, 1600);
    } catch (error) {
      this.submitLabel.textContent = restingLabel;
      this.submitBtn.removeAttribute('aria-disabled');
      this.showError((error && error.description) || (error && error.message) || 'Something went wrong. Please try again.');
    }
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  hideError() {
    if (this.errorEl) this.errorEl.hidden = true;
  }
}

if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
